import { CapsuleGeometry, CylinderGeometry, ExtrudeGeometry, Group, Matrix4, PointLight, Quaternion, Shape, Vector3 } from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { createCabinBuilder, grid, rail, type CabinAssembly, type CabinPanel } from './CabinPrimitives';
import { createWindshield } from './Windshield';
export type { CabinAssembly, CabinPanel } from './CabinPrimitives';

function roundedRectangle(w: number, h: number, r: number) {
  const x = -w / 2, y = -h / 2, path = new Shape();
  path.moveTo(x + r, y); path.lineTo(x + w - r, y); path.quadraticCurveTo(x + w, y, x + w, y + r);
  path.lineTo(x + w, y + h - r); path.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  path.lineTo(x + r, y + h); path.quadraticCurveTo(x, y + h, x, y + h - r);
  path.lineTo(x, y + r); path.quadraticCurveTo(x, y, x + r, y); return path;
}

/** One coherent survey vessel: a deep window surround, a low saddle console,
 * a single integrated display and hand controls at the pilot's armrests. */
export function buildCabin(width: number, height: number): CabinAssembly {
  const aspect = width / height, compact = aspect < 1.15;
  const { root, p, add, plate, part, finish } = createCabinBuilder(aspect);
  const bezelAspect = 5.2;
  const pixelsWide = compact ? width * .78 : Math.min(width * .52, height * (height < 330 ? .62 : .68));
  const bottomInset = compact ? 150 : height < 330 ? 68 : height < 501 ? 76 : 104;
  const panelY = -1 + (bottomInset + pixelsWide / bezelAspect / 2) * 2 / height;
  const screenDepth = 2.7, w = pixelsWide * 2 * screenDepth / height, h = w / bezelAspect;
  const center = p(0, panelY, screenDepth), uAxis = new Vector3(1, 0, 0), vAxis = new Vector3(0, 1, -.28).normalize();
  const normal = uAxis.clone().cross(vAxis).normalize();
  const basis = new Matrix4().makeBasis(uAxis, vAxis, normal).setPosition(center);
  const face = (u: number, v: number, depth = 0) => center.clone().addScaledVector(uAxis, u * w).addScaledVector(vAxis, v * h).addScaledVector(normal, depth);
  const sillY = Math.min(-.22, panelY + pixelsWide / bezelAspect * 2 / height * .80);

  // Continuous windscreen opening: close roof, far lower seal. The deep return
  // between this opening and the side walls is visible at the outer corners.
  const roof = (x: number) => p(x * .985, .91 + (1 - x * x) * .15, 1.45 + Math.abs(x) * .22);
  const sill = (x: number) => p(x * .88, sillY - Math.pow(Math.abs(x), 1.6) * .17, 4.75 - Math.abs(x) * .35);
  const rimPoints = [-1, -.80, -.5, 0, .5, .80, 1].map(roof);
  const gauge = compact ? .064 : .11;
  add(rail(rimPoints, gauge * 1.7, gauge * 2.0, 42), 'seal');
  add(rail(rimPoints.map(point => point.clone().multiplyScalar(.973)), gauge * 1.2, gauge * 1.15, 42), 'shell');
  add(rail(rimPoints.map(point => point.clone().multiplyScalar(.968).add(new Vector3(0, -.018, .015))), gauge * .20, gauge * .24, 42), 'armor');
  // A curved underside above the pilot supplies a near foreground plane.
  add(grid(28, 6, (u, v) => {
    const x = u * 2 - 1;
    return roof(x).clone().lerp(p(x * 1.35, 1.18 + (1 - x * x) * .12, .62), v);
  }), 'seal');
  add(rail([p(-.40, 1.06, .91), p(0, 1.10, .81), p(.40, 1.06, .91)], .055, .08, 20), 'detail');

  for (const side of [-1, 1]) {
    const top = roof(side), foot = sill(side);
    const edge = [0, .20, .55, 1].map(t => top.clone().lerp(foot, t));
    add(rail(edge, gauge * 1.45, gauge * 2.35, 24), 'seal');
    add(rail(edge.map(point => point.clone().multiplyScalar(.974)), gauge * 1.0, gauge * 1.4, 24), 'shell');
    const tangent = foot.clone().sub(top).normalize();
    const cross = new Vector3(tangent.y, -tangent.x, 0).normalize();
    const front = tangent.clone().cross(cross).negate().normalize();
    const edgePoint = (t: number, inset: number) => top.clone().lerp(foot, t).multiplyScalar(.968)
      .addScaledVector(cross, side * gauge * inset).addScaledVector(front, gauge * .80);
    add(rail([edgePoint(.02, .31), edgePoint(.98, .31)], gauge * .17, gauge * .19, 16), 'armor');
    // Long, broad light inserts follow the pillars with a small service break.
    const lightPoint = (t: number) => top.clone().lerp(foot, t).multiplyScalar(.91)
      .add(new Vector3(-side * gauge * .65, 0, .035));
    for (const [a, b] of [[.035, .48], [.505, .97]]) {
      add(rail([lightPoint(a), lightPoint(b)], gauge * .26, gauge * .18, 12), 'glow');
    }
    for (const t of [.08, .90]) {
      const screw = new CylinderGeometry(gauge * .16, gauge * .16, gauge * .12, 8);
      screw.applyQuaternion(new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), front));
      screw.translate(...edgePoint(t, .05).addScaledVector(front, gauge * .09).toArray()); add(screw, 'detail');
    }
    // Wall returns run back toward the seat. Their width and depth, not a
    // screen-sized outline, make the pilot feel inside an enclosed vessel.
    const outer = [p(side * 1.24, 1.12, .87), p(side * 1.23, .40, 1.05), p(side * 1.17, -.78, 1.45), p(side * 1.10, -1.3, 1.1)];
    const wall = (point: Vector3) => point.clone().multiplyScalar(1.10).add(new Vector3(side * gauge * .80, 0, 0));
    plate([top, outer[0], outer[1], edge[1]].map(wall), 'shell', .08, .008);
    plate([edge[1], outer[1], outer[2], edge[2]].map(wall), 'seal', .08, .008);
    plate([edge[2], outer[2], outer[3], foot].map(wall), 'shell', .08, .008);

    const deckRear = face(side * .62, .72, -.10);
    const lower = p(side * (compact ? .96 : .70), -1.26, 1.28), far = sill(side * .83);
    const shoulder = p(side * (compact ? 1.04 : .83), -1.13, 1.40);
    plate([far, sill(side), p(side * 1.06, -.98, 1.45), shoulder, deckRear], 'shell', .12, .018);
    plate([face(side * .61, .73, -.10), face(side * .64, -.77, -.10), lower, shoulder, far], 'seal', .10, .012);
    // Brushed shoulder facing and a padded armrest establish human scale.
    plate([sill(side * .94).multiplyScalar(.99), p(side * .98, -.78, 2.05), p(side * .96, -1.2, 1.28), p(side * .80, -1.16, 1.37)], 'detail', .06, .016);
    const pad = new RoundedBoxGeometry(aspect * .32, .10, .69, 3, .045);
    pad.rotateX(-.12); pad.rotateZ(-side * .05); pad.translate(...p(side * .83, -.98, 1.30).toArray()); add(pad, 'seal');
    for (let i = 0; i < 6; i++) {
      const a = sill(side * (.66 + i * .035)).lerp(deckRear, .32);
      add(rail([a, a.clone().add(new Vector3(side * .065, -.025, .19))], .023, .018, 2), 'seal');
    }
    const lamp = new PointLight('#9c968a', .75, 4.1, 2);
    lamp.position.copy(face(side * .50, 1.25, .8)); lamp.name = 'Console indirect light'; root.add(lamp);
  }

  // The complete console wraps behind one display, then descends toward the
  // pilot. Its screen well and lower spine are part of the same assembly.
  const chassis = new ExtrudeGeometry(roundedRectangle(w * 1.27, h * 1.72, h * .16), {
    depth: .32, bevelEnabled: true, bevelSegments: 3, bevelSize: .025, bevelThickness: .025, curveSegments: 10, steps: 1,
  });
  chassis.translate(0, 0, -.42); chassis.applyMatrix4(basis); add(chassis, 'shell');
  add(grid(24, 7, (u, v) => {
    const x = u * 2 - 1;
    return face(x * .62, .78, -.12).lerp(sill(x * .84), v).add(new Vector3(0, Math.sin(v * Math.PI) * .045, 0));
  }), 'shell');
  add(rail([-1, -.6, 0, .6, 1].map(x => face(x * .63, .84, -.075)), .035, .065, 28), 'seal');
  // Machined recessed bezel with rounded corners and a real open centre.
  const bezel = roundedRectangle(w * 1.050, h * 1.17, h * .10);
  bezel.holes.push(roundedRectangle(w * 1.012, h * 1.035, h * .035));
  const bezelGeometry = new ExtrudeGeometry(bezel, { depth: .027, bevelEnabled: true, bevelSegments: 3, bevelSize: .008, bevelThickness: .008, curveSegments: 10 });
  bezelGeometry.translate(0, 0, -.016); bezelGeometry.applyMatrix4(basis); add(bezelGeometry, 'detail');
  add(rail([face(-.54, -.68, .01), face(.54, -.68, .01)], .016, .026, 2), 'armor');
  for (let i = 0; i < 7; i++) {
    const key = new RoundedBoxGeometry(w * .070, h * .12, .028, 2, .009);
    key.translate((-3 + i) * w * .145, -h * .79, -.004); key.applyMatrix4(basis); add(key, i === 0 ? 'amber' : 'seal');
  }
  const lowerLeft = p(-.24, -1.30, 1.10), lowerRight = p(.24, -1.30, 1.10);
  plate([face(-.48, -.96, -.19), face(.48, -.96, -.19), lowerRight, lowerLeft], 'shell', .12, .022);
  add(rail([face(-.49, -.96, -.17), lowerLeft], .027, .045, 2), 'detail');
  add(rail([face(.49, -.96, -.17), lowerRight], .027, .045, 2), 'detail');
  const screen = (u: number, v: number) => face(u - .5, .5 - v, .004);
  const panel: CabinPanel = { geometry: grid(1, 1, screen), samples: [.015, .5, .985].flatMap(u => [.015, .5, .985].map(v => screen(u, v))), aspect: bezelAspect };

  // A low far seal and two short service lights connect the central console
  // to the distant windscreen, leaving open space above both armrests.
  const floorPoints = [-1, -.7, -.3, 0, .3, .7, 1].map(sill);
  add(rail(floorPoints, .060, .11, 36), 'seal');
  add(rail(floorPoints.map(point => point.clone().add(new Vector3(0, .017, .022))), .014, .022, 36), 'armor');
  for (const side of [-1, 1]) add(rail([sill(side * .61).multiplyScalar(.99), sill(side * .80).multiplyScalar(.99)], .012, .018, 8), 'glow');

  // Articulated hand controls use rounded industrial forms and rubber boots.
  const stickMount = new Group(); stickMount.position.copy(p(compact ? .91 : .69, compact ? -1.05 : -.91, 1.65)); root.add(stickMount);
  if (compact) stickMount.scale.setScalar(.65);
  const stick = new Group(); stick.name = 'pilot_sidestick'; stickMount.add(stick);
  for (let i = 0; i < 5; i++) {
    const boot = new CylinderGeometry(.13 - i * .014, .15 - i * .014, .027, 20); boot.translate(0, i * .028, 0); part(boot, 'seal', stickMount);
  }
  const stem = new CylinderGeometry(.034, .048, .22, 16); stem.translate(0, .17, 0); part(stem, 'armor', stick);
  const grip = new CapsuleGeometry(.075, .19, 5, 16); grip.rotateZ(-.20); grip.translate(.02, .34, .015); part(grip, 'seal', stick);
  const gripEdge = new CapsuleGeometry(.015, .19, 4, 10); gripEdge.rotateZ(-.20); gripEdge.translate(-.035, .34, .062); part(gripEdge, 'detail', stick);
  part(rail([new Vector3(-.055, .18, -.02), new Vector3(-.09, .35, -.10), new Vector3(.015, .46, -.10)], .021, .026, 16), 'detail', stick);
  const head = new RoundedBoxGeometry(.18, .085, .13, 3, .025); head.rotateZ(-.18); head.translate(.055, .48, .025); part(head, 'shell', stick);
  const trigger = new RoundedBoxGeometry(.072, .11, .033, 2, .014); trigger.translate(.008, .33, -.072); part(trigger, 'detail', stick);
  const thumb = new CylinderGeometry(.026, .029, .022, 16); thumb.translate(.084, .524, .045); part(thumb, 'amber', stick);

  const throttleMount = new Group(); throttleMount.position.copy(p(compact ? -.91 : -.69, compact ? -1.05 : -.91, 1.65)); root.add(throttleMount);
  if (compact) throttleMount.scale.setScalar(.65);
  const track = new RoundedBoxGeometry(.26, .05, .54, 3, .023); track.translate(0, .015, -.04); part(track, 'seal', throttleMount);
  for (const side of [-1, 1]) {
    const runner = new RoundedBoxGeometry(.021, .025, .42, 2, .008); runner.translate(side * .086, .048, -.05); part(runner, 'armor', throttleMount);
  }
  const throttle = new Group(); throttle.name = 'pilot_throttle'; throttleMount.add(throttle);
  const lever = new CylinderGeometry(.035, .045, .16, 12); lever.translate(0, .12, 0); part(lever, 'detail', throttle);
  const throttleGrip = new RoundedBoxGeometry(.29, .13, .16, 3, .045); throttleGrip.rotateX(-.15); throttleGrip.translate(0, .22, 0); part(throttleGrip, 'seal', throttle);
  const cap = new RoundedBoxGeometry(.20, .015, .075, 2, .007); cap.translate(0, .29, -.01); part(cap, 'amber', throttle);

  const cabin = finish();
  const glass = createWindshield(grid(36, 24, (u, v) => {
    const x = u * 2 - 1;
    return roof(x).lerp(sill(x), v).add(new Vector3(0, 0, -.025 - Math.sin(u * Math.PI) * Math.sin(v * Math.PI) * .055));
  }));
  root.add(glass);
  return { ...cabin, panel, compact, windshield: glass.material, stick, throttle };
}
